import { cleanContext } from './contextKeywords';

const DOMAIN_RE = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;

/** Broken / redundant tokens from bilingual name queries (not contextual entities). */
const GENERIC_NAME_NOISE = new Set(
  [
    'ro',
    'en',
    'persoana',
    'persona',
    'person',
    'nume',
    'name',
    'profil',
    'profile',
    'search',
    'google',
    'result',
    'results',
    'linkedin',
    'facebook',
    'www',
    'http',
    'https',
    'com',
    'org',
    'net',
  ].map((w) => w.toLowerCase())
);

/** Common Romanian/English news filler — not useful as card chips. */
const NEWS_STOPWORDS = new Set(
  [
    'schimbare',
    'schimbari',
    'pandemia',
    'pandemie',
    'sector',
    'sectoare',
    'anul',
    'anului',
    'azi',
    'astazi',
    'ieri',
    'despre',
    'pentru',
    'dupa',
    'după',
    'este',
    'sunt',
    'fost',
    'face',
    'facut',
    'făcut',
    'mare',
    'mari',
    'nou',
    'noi',
    'noua',
    'nouă',
    'primul',
    'prima',
    'ultimul',
    'ultima',
    'intre',
    'între',
    'prin',
    'care',
    'acest',
    'aceasta',
    'această',
    'also',
    'said',
    'says',
    'will',
    'year',
    'years',
    'today',
    'news',
    'article',
    'press',
    'report',
    'according',
    'more',
    'than',
    'from',
    'with',
    'that',
    'this',
    'have',
    'been',
    'were',
    'their',
    'about',
  ].map((w) => w.toLowerCase())
);

/**
 * Synonym clusters for NLP web-lead chips — keep one canonical label per family.
 * Example: NAVIGATION wins over MAPPING / ROAD / DIRECTIONS.
 */
const TAG_SYNONYM_GROUPS = [
  {
    canonical: 'NAVIGATION',
    members: new Set([
      'navigation',
      'mapping',
      'road',
      'roads',
      'directions',
      'direction',
      'maps',
      'map',
      'route',
      'routes',
      'gps',
      'wayfinding',
    ]),
  },
  {
    canonical: 'LOCATION',
    members: new Set([
      'location',
      'address',
      'place',
      'places',
      'geo',
      'geolocation',
      'locality',
      'area',
    ]),
  },
  {
    canonical: 'CONTACT',
    members: new Set(['contact', 'contacts', 'reach', 'reachability']),
  },
  {
    canonical: 'PHONE',
    members: new Set(['phone', 'telephone', 'tel', 'mobile', 'cellphone', 'call']),
  },
  {
    canonical: 'EMAIL',
    members: new Set(['email', 'e-mail', 'mail', 'mailbox']),
  },
];

function bareTagToken(tag) {
  return String(tag || '')
    .replace(/^[\s\p{Emoji_Presentation}\p{Extended_Pictographic}\p{Symbol}]+/u, '')
    .trim()
    .toLowerCase();
}

function synonymGroupForTag(tag) {
  const bare = bareTagToken(tag);
  if (!bare) return null;
  return TAG_SYNONYM_GROUPS.find((g) => g.members.has(bare)) || null;
}

/**
 * Deduplicate synonym / exact-duplicate feed badges and cap the list.
 *
 * @param {unknown[]} tags
 * @param {number} [max=3]
 * @returns {string[]}
 */
export function curateFeedBadges(tags, max = 3) {
  const list = Array.isArray(tags) ? tags : [];
  const out = [];
  const seenExact = new Set();
  const seenSynonym = new Set();
  const limit = Math.max(0, Number(max) || 0);

  for (const raw of list) {
    if (out.length >= limit) break;
    const s = String(raw || '').trim();
    if (!s) continue;
    const exactKey = s.toLowerCase();
    if (seenExact.has(exactKey)) continue;

    const group = synonymGroupForTag(s);
    if (group) {
      if (seenSynonym.has(group.canonical)) continue;
      seenSynonym.add(group.canonical);
      const bare = bareTagToken(s);
      const prefixMatch = String(s).match(
        /^([\s\p{Emoji_Presentation}\p{Extended_Pictographic}\p{Symbol}]+)/u,
      );
      const prefix = prefixMatch ? prefixMatch[1].trim() : '';
      const preferred =
        bare === group.canonical.toLowerCase()
          ? s
          : `${prefix ? `${prefix} ` : ''}${group.canonical}`.trim();
      seenExact.add(preferred.toLowerCase());
      out.push(preferred);
      continue;
    }

    seenExact.add(exactKey);
    out.push(s);
  }

  return out;
}

function nameTokenBlocklist(searchName) {
  const parts = String(searchName || '')
    .trim()
    .toLowerCase()
    .split(/[\s,.;|/]+/)
    .map((w) => w.replace(/[^a-z0-9ăâîșțáéíóúüöß-]/gi, ''))
    .filter((w) => w.length >= 2);
  return new Set([...parts, ...GENERIC_NAME_NOISE, ...NEWS_STOPWORDS]);
}

function tokenize(raw, searchName, maxTokens = 8) {
  const cleaned = cleanContext(String(raw || ''), searchName || '');
  if (!cleaned) return [];
  const blocked = nameTokenBlocklist(searchName);
  const parts = cleaned
    .split(/[\s,;|/•·–—]+/)
    .map((w) => w.replace(/^[^a-z0-9@+#ăâîșț]+|[^a-z0-9@+#ăâîșț]+$/gi, ''))
    .filter((w) => {
      if (!w || w.length < 3 || w.length > 28) return false;
      return !blocked.has(w.toLowerCase());
    });
  return parts.slice(0, maxTokens);
}

function hostKeyword(url) {
  try {
    const host = new URL(url).hostname.replace(/^www\./i, '');
    if (!host || !DOMAIN_RE.test(host)) return null;
    const label = host.split('.').filter(Boolean)[0];
    return label && label.length >= 2 ? label : host;
  } catch {
    return null;
  }
}

function isBlockedKeyword(val, blocked) {
  const s = String(val || '').trim();
  if (!s) return true;
  const lower = s.toLowerCase();
  if (blocked.has(lower)) return true;
  // Drop multi-token chips that are only name fragments.
  const tokens = lower.split(/\s+/).filter(Boolean);
  if (tokens.length && tokens.every((t) => blocked.has(t))) return true;
  return false;
}

/** Extract display keywords from a feed result row (no raw snippet block). */
export function extractResultKeywords(res, max = 3) {
  if (!res || typeof res !== 'object') return [];
  const searchName = res.target_name || res.query_name || res.entity_checked || '';
  const blocked = nameTokenBlocklist(searchName);
  const candidates = [];
  const seen = new Set();

  const push = (val) => {
    const s = String(val || '').trim();
    if (!s || s.length < 2 || seen.has(s.toLowerCase())) return;
    if (isBlockedKeyword(s, blocked)) return;
    // Skip ui_bucket noise like "compliance alert"
    if (/^(compliance|google_dork|serp)/i.test(s.replace(/\s+/g, '_'))) return;
    seen.add(s.toLowerCase());
    candidates.push(s);
  };

  (res.matched_tags || []).forEach((t) => push(t));
  if (res.platform) push(res.platform);
  if (res.category && res.category !== 'Social') push(res.category);

  const host = hostKeyword(res.url);
  if (host && !GENERIC_NAME_NOISE.has(host.toLowerCase())) push(host);

  tokenize(`${res.title || ''} ${res.snippet || ''}`, searchName, max * 2).forEach((t) => {
    push(t);
  });

  return curateFeedBadges(candidates, max);
}

/** Format calendar dates for cards; scan-relative +Xs offsets are omitted (not user-facing). */
export function formatDiscoveryOffset(res) {
  if (!res || typeof res !== 'object') return null;
  const metaDate = res.source_date || res.published_at || res.date;
  if (typeof metaDate === 'string' && metaDate.trim()) {
    const trimmed = metaDate.trim();
    if (/^\d{4}/.test(trimmed) || /[a-z]{3}/i.test(trimmed)) return trimmed.slice(0, 32);
  }
  return null;
}
